/**
 * Where a policy is filed. Shared by the single upload and the batch, so a
 * hundred files can be filed at once instead of a hundred visits to the
 * record afterwards.
 *
 * WHAT MOVED OUT, AND WHY. This used to carry "Who must acknowledge this
 * policy?" as well, which put the audience ABOVE the switch that decides
 * whether anybody is asked at all — so the page offered a choice between two
 * audiences and then, further down, asked whether the question applied. The
 * audience now lives with the switch that governs it, in
 * ReviewAndApproval.tsx. Nothing about what the backend stores changed.
 */

import type { TaxonomyItem } from '../admin/useTaxonomy';
import own from './UploadPage.module.css';

/** Kept here because the upload pages and the backend both speak it. */
export type Audience = 'all' | 'departments';

interface Props {
  idPrefix?: string;
  plural?: boolean;
  departments: TaxonomyItem[] | undefined;
  categories: TaxonomyItem[] | undefined;
  docTypes: TaxonomyItem[] | undefined;
  departmentId: string;
  onDepartmentId: (v: string) => void;
  categoryId: string;
  onCategoryId: (v: string) => void;
  documentTypeId: string;
  onDocumentTypeId: (v: string) => void;
  /** Shown once the person has tried to go on without choosing one. */
  documentTypeError?: string | null;
  disabled?: boolean;
}

export function TaxonomyFields({
  idPrefix = 'policy', plural = false, departments, categories, docTypes,
  departmentId, onDepartmentId, categoryId, onCategoryId, documentTypeId, onDocumentTypeId,
  documentTypeError, disabled,
}: Props) {
  const id = (s: string) => `${idPrefix}-${s}`;
  return (
    <>
      {/*
        * REQUIRED, and first (owner, 2026-10-04). Approval routing and the
        * library's type filter both key on it, and a policy with none could
        * only ever fall to the catch-all workflow. The server refuses a new
        * policy without one; this is that rule said before the click.
        */}
      <div className={own.field}>
        <label className={own.label} htmlFor={id('doctype')}>Document type</label>
        <select id={id('doctype')} className={own.select} value={documentTypeId}
          onChange={(e) => onDocumentTypeId(e.target.value)} disabled={disabled}
          aria-invalid={documentTypeError ? true : undefined}
          aria-describedby={id('doctype-hint')}>
          <option value="">Choose a type</option>
          {(docTypes ?? []).filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {documentTypeError
          ? <p className={own.error} id={id('doctype-hint')} role="alert">{documentTypeError}</p>
          : (
            <p className={own.hint} id={id('doctype-hint')}>
              {docTypes && docTypes.filter((t) => t.isActive).length === 0
                ? 'There are no document types yet. An administrator adds them under Taxonomy.'
                : `Policy, procedure, guideline, plan. Decides which approval workflow ${plural ? 'they follow' : 'it follows'}.`}
            </p>
          )}
      </div>
      {/* The rest are optional, because a file often arrives before anyone has
          decided where it belongs; they can be set on the record. */}
      <div className={own.field}>
        <label className={own.label} htmlFor={id('department')}>
          Department <span className={own.optional}>optional</span>
        </label>
        <select id={id('department')} className={own.select} value={departmentId} onChange={(e) => onDepartmentId(e.target.value)} disabled={disabled}>
          <option value="">Not set</option>
          {(departments ?? []).filter((d) => d.isActive).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <p className={own.hint}>Who owns {plural ? 'them' : 'it'}, and whose staff can be asked to acknowledge {plural ? 'them' : 'it'}.</p>
      </div>
      <div className={own.field}>
        <label className={own.label} htmlFor={id('category')}>
          Category <span className={own.optional}>optional</span>
        </label>
        <select id={id('category')} className={own.select} value={categoryId} onChange={(e) => onCategoryId(e.target.value)} disabled={disabled}>
          <option value="">Not set</option>
          {(categories ?? []).filter((c) => c.isActive).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
    </>
  );
}
